import { PlateRegionSet } from '../plate-emblems';
import { AT_STATE_ARMS } from './at-arms';

/**
 * Austrian district codes (Bezirkskürzel) → Bundesland, generated from Albert's
 * `AT District abbrev and Coat of Arms.xlsx` (CRM requirement mail, 2026-07-29).
 *
 * Three jobs, all from that mail:
 *
 *  1. Decides *which* coat of arms sits between the district code and the number. On a real
 *     Austrian plate that emblem is the arms of the **Bundesland** — a Linz ("L") plate carries
 *     the Upper Austrian arms. Never the national red-white-red, which is what the plate
 *     component drew before: *"after the district you MUST NOT show the Austrian flag."*
 *  2. Validates the district code itself — *"also for checking, if the number is correct"*.
 *  3. Carries `maxChars`, the spreadsheet's "Max. characters after coat of arms" (5 for most
 *     districts, 6 for the single-letter city codes), so an over-long number can be caught
 *     client-side.
 *
 * The special series at the end (BP police, BH military, diplomatic and consular corps) are real
 * codes but belong to no Bundesland, so they validate as known while rendering no arms.
 *
 * **Generated — do not hand-edit.** To refresh from a newer spreadsheet, re-run
 * `gen_arms.py`: the district rows are columns A–D of sheet 1, and the nine arms are Excel
 * rich-value images on the legend rows at the bottom, resolved through `xl/metadata.xml` →
 * `rdrichvalue.xml` → `richValueRel.xml` rather than through the drawing.
 */

/** The nine Bundesländer, keyed by their conventional abbreviation. */
export type AtState = 'W' | 'NOE' | 'OOE' | 'SBG' | 'STMK' | 'K' | 'T' | 'VBG' | 'BGLD';

/** Austria's region data, attached to the `AT` entry of `PLATE_COUNTRIES`. */
export const AT_REGIONS: PlateRegionSet = {
  emblems: AT_STATE_ARMS,
  districts: {
    // ── Niederösterreich ──
    AM: { name: 'Amstetten', emblem: 'NOE', maxChars: 5 },
    // ── Vorarlberg ──
    B: { name: 'Bregenz', emblem: 'VBG', maxChars: 6 },
    // ── Steiermark ──
    BA: { name: 'Bad Aussee (Expositur)', emblem: 'STMK', maxChars: 5 },
    // ── Niederösterreich ──
    BK: { name: 'Bruck an der Leitha', emblem: 'NOE', maxChars: 5 },
    BL: { name: 'Bruck an der Leitha (alt: Bruck/Leitha)', emblem: 'NOE', maxChars: 5 },
    // ── Steiermark ──
    BM: { name: 'Bruck-Mürzzuschlag', emblem: 'STMK', maxChars: 5 },
    // ── Niederösterreich ──
    BN: { name: 'Baden', emblem: 'NOE', maxChars: 5 },
    // ── Oberösterreich ──
    BR: { name: 'Braunau am Inn', emblem: 'OOE', maxChars: 5 },
    // ── Vorarlberg ──
    BZ: { name: 'Bludenz', emblem: 'VBG', maxChars: 5 },
    // ── Steiermark ──
    DL: { name: 'Deutschlandsberg', emblem: 'STMK', maxChars: 5 },
    // ── Vorarlberg ──
    DO: { name: 'Dornbirn', emblem: 'VBG', maxChars: 5 },
    // ── Burgenland ──
    E: { name: 'Eisenstadt', emblem: 'BGLD', maxChars: 6 },
    EE: { name: 'Eisenstadt-Umgebung', emblem: 'BGLD', maxChars: 5 },
    // ── Oberösterreich ──
    EF: { name: 'Eferding', emblem: 'OOE', maxChars: 5 },
    // ── Burgenland ──
    EU: { name: 'Eisenstadt-Umgebung', emblem: 'BGLD', maxChars: 5 },
    // ── Kärnten ──
    FE: { name: 'Feldkirchen', emblem: 'K', maxChars: 5 },
    // ── Vorarlberg ──
    FK: { name: 'Feldkirch', emblem: 'VBG', maxChars: 5 },
    // ── Oberösterreich ──
    FR: { name: 'Freistadt', emblem: 'OOE', maxChars: 5 },
    // ── Steiermark ──
    G: { name: 'Graz', emblem: 'STMK', maxChars: 6 },
    GB: { name: 'Gröbming (Expositur)', emblem: 'STMK', maxChars: 5 },
    // ── Niederösterreich ──
    GD: { name: 'Gmünd', emblem: 'NOE', maxChars: 5 },
    GF: { name: 'Gänserndorf', emblem: 'NOE', maxChars: 5 },
    // ── Oberösterreich ──
    GM: { name: 'Gmunden', emblem: 'OOE', maxChars: 5 },
    GR: { name: 'Grieskirchen', emblem: 'OOE', maxChars: 5 },
    // ── Steiermark ──
    GS: { name: 'Güssing', emblem: 'STMK', maxChars: 5 },
    GU: { name: 'Graz-Umgebung', emblem: 'STMK', maxChars: 5 },
    // ── Salzburg ──
    HA: { name: 'Hallein', emblem: 'SBG', maxChars: 5 },
    // ── Kärnten ──
    HE: { name: 'Hermagor', emblem: 'K', maxChars: 5 },
    // ── Steiermark ──
    HF: { name: 'Hartberg-Fürstenfeld', emblem: 'STMK', maxChars: 5 },
    // ── Niederösterreich ──
    HL: { name: 'Hollabrunn', emblem: 'NOE', maxChars: 5 },
    HO: { name: 'Horn', emblem: 'NOE', maxChars: 5 },
    // ── Tirol ──
    I: { name: 'Innsbruck', emblem: 'T', maxChars: 6 },
    IL: { name: 'Innsbruck-Land', emblem: 'T', maxChars: 5 },
    IM: { name: 'Imst', emblem: 'T', maxChars: 5 },
    // ── Burgenland ──
    JE: { name: 'Jennersdorf', emblem: 'BGLD', maxChars: 5 },
    // ── Salzburg ──
    JO: { name: 'St. Johann im Pongau', emblem: 'SBG', maxChars: 5 },
    // ── Steiermark ──
    JU: { name: 'Judenburg (jetzt MT)', emblem: 'STMK', maxChars: 5 },
    // ── Kärnten ──
    K: { name: 'Klagenfurt am Wörthersee', emblem: 'K', maxChars: 6 },
    // ── Tirol ──
    KB: { name: 'Kitzbühel', emblem: 'T', maxChars: 5 },
    // ── Oberösterreich ──
    KI: { name: 'Kirchdorf an der Krems', emblem: 'OOE', maxChars: 5 },
    // ── Kärnten ──
    KL: { name: 'Klagenfurt-Land', emblem: 'K', maxChars: 5 },
    // ── Niederösterreich ──
    KO: { name: 'Korneuburg', emblem: 'NOE', maxChars: 5 },
    KR: { name: 'Krems/Donau (Land)', emblem: 'NOE', maxChars: 5 },
    KS: { name: 'Krems/Donau (Stadt)', emblem: 'NOE', maxChars: 6 },
    // ── Tirol ──
    KU: { name: 'Kufstein', emblem: 'T', maxChars: 5 },
    // ── Oberösterreich ──
    L: { name: 'Linz', emblem: 'OOE', maxChars: 6 },
    // ── Tirol ──
    LA: { name: 'Landeck', emblem: 'T', maxChars: 5 },
    // ── Steiermark ──
    LB: { name: 'Leibnitz', emblem: 'STMK', maxChars: 5 },
    LE: { name: 'Leoben (Stadt)', emblem: 'STMK', maxChars: 6 },
    // ── Niederösterreich ──
    LF: { name: 'Lilienfeld', emblem: 'NOE', maxChars: 5 },
    // ── Steiermark ──
    LI: { name: 'Liezen', emblem: 'STMK', maxChars: 5 },
    // ── Oberösterreich ──
    LL: { name: 'Linz-Land', emblem: 'OOE', maxChars: 5 },
    // ── Steiermark ──
    LN: { name: 'Leoben (Land)', emblem: 'STMK', maxChars: 5 },
    // ── Tirol ──
    LZ: { name: 'Lienz', emblem: 'T', maxChars: 5 },
    // ── Burgenland ──
    MA: { name: 'Mattersburg', emblem: 'BGLD', maxChars: 5 },
    // ── Niederösterreich ──
    MD: { name: 'Mödling', emblem: 'NOE', maxChars: 5 },
    ME: { name: 'Melk', emblem: 'NOE', maxChars: 5 },
    MI: { name: 'Mistelbach', emblem: 'NOE', maxChars: 5 },
    // ── Steiermark ──
    MT: { name: 'Murtal', emblem: 'STMK', maxChars: 5 },
    MU: { name: 'Murau', emblem: 'STMK', maxChars: 5 },
    MZ: { name: 'Mürzzuschlag (jetzt BM)', emblem: 'STMK', maxChars: 5 },
    // ── Burgenland ──
    ND: { name: 'Neusiedl am See', emblem: 'BGLD', maxChars: 5 },
    // ── Niederösterreich ──
    NK: { name: 'Neunkirchen', emblem: 'NOE', maxChars: 5 },
    // ── Burgenland ──
    OP: { name: 'Oberpullendorf', emblem: 'BGLD', maxChars: 5 },
    OW: { name: 'Oberwart', emblem: 'BGLD', maxChars: 5 },
    // ── Niederösterreich ──
    P: { name: 'St. Pölten (Stadt)', emblem: 'NOE', maxChars: 6 },
    // ── Oberösterreich ──
    PE: { name: 'Perg', emblem: 'OOE', maxChars: 5 },
    // ── Niederösterreich ──
    PL: { name: 'St. Pölten (Land)', emblem: 'NOE', maxChars: 5 },
    // ── Tirol ──
    RE: { name: 'Reutte', emblem: 'T', maxChars: 5 },
    // ── Oberösterreich ──
    RI: { name: 'Ried im Innkreis', emblem: 'OOE', maxChars: 5 },
    RO: { name: 'Rohrbach', emblem: 'OOE', maxChars: 5 },
    // ── Salzburg ──
    S: { name: 'Salzburg (Stadt)', emblem: 'SBG', maxChars: 6 },
    // ── Niederösterreich ──
    SB: { name: 'Scheibbs', emblem: 'NOE', maxChars: 5 },
    // ── Oberösterreich ──
    SD: { name: 'Schärding', emblem: 'OOE', maxChars: 5 },
    SE: { name: 'Steyr-Land', emblem: 'OOE', maxChars: 5 },
    // ── Salzburg ──
    SL: { name: 'Salzburg-Umgebung', emblem: 'SBG', maxChars: 5 },
    // ── Kärnten ──
    SP: { name: 'Spittal an der Drau', emblem: 'K', maxChars: 5 },
    // ── Oberösterreich ──
    SR: { name: 'Steyr (Stadt)', emblem: 'OOE', maxChars: 6 },
    // ── Steiermark ──
    ST: { name: 'Schladming (Expositur)', emblem: 'STMK', maxChars: 5 },
    // ── Kärnten ──
    SV: { name: 'St. Veit an der Glan', emblem: 'K', maxChars: 5 },
    // ── Niederösterreich ──
    SW: { name: 'Schwechat (Stadt)', emblem: 'NOE', maxChars: 5 },
    // ── Tirol ──
    SZ: { name: 'Schwaz', emblem: 'T', maxChars: 5 },
    // ── Salzburg ──
    TA: { name: 'Tamsweg', emblem: 'SBG', maxChars: 5 },
    // ── Niederösterreich ──
    TU: { name: 'Tulln', emblem: 'NOE', maxChars: 5 },
    // ── Oberösterreich ──
    UU: { name: 'Urfahr-Umgebung', emblem: 'OOE', maxChars: 5 },
    VB: { name: 'Vöcklabruck', emblem: 'OOE', maxChars: 5 },
    // ── Kärnten ──
    VI: { name: 'Villach (Stadt)', emblem: 'K', maxChars: 6 },
    VK: { name: 'Völkermarkt', emblem: 'K', maxChars: 5 },
    VL: { name: 'Villach-Land', emblem: 'K', maxChars: 5 },
    // ── Steiermark ──
    VO: { name: 'Voitsberg', emblem: 'STMK', maxChars: 5 },
    // ── Wien ──
    W: { name: 'Wien', emblem: 'W', maxChars: 6 },
    // ── Niederösterreich ──
    WB: { name: 'Wiener Neustadt (Land)', emblem: 'NOE', maxChars: 5 },
    // ── Oberösterreich ──
    WE: { name: 'Wels (Stadt)', emblem: 'OOE', maxChars: 6 },
    WL: { name: 'Wels-Land', emblem: 'OOE', maxChars: 5 },
    // ── Niederösterreich ──
    WN: { name: 'Wiener Neustadt (Stadt)', emblem: 'NOE', maxChars: 6 },
    // ── Kärnten ──
    WO: { name: 'Wolfsberg', emblem: 'K', maxChars: 5 },
    // ── Niederösterreich ──
    WT: { name: 'Waidhofen an der Thaya', emblem: 'NOE', maxChars: 5 },
    WU: { name: 'Wien-Umgebung (aufgelöst)', emblem: 'NOE', maxChars: 5 },
    WY: { name: 'Waidhofen an der Ybbs', emblem: 'NOE', maxChars: 6 },
    // ── Steiermark ──
    WZ: { name: 'Weiz', emblem: 'STMK', maxChars: 5 },
    // ── Salzburg ──
    ZE: { name: 'Zell am See', emblem: 'SBG', maxChars: 5 },
    // ── Niederösterreich ──
    ZT: { name: 'Zwettl', emblem: 'NOE', maxChars: 5 },
    // ── Sonderkennzeichen — no Bundesland, no arms ──
    BD: { name: 'Diplomatisches Korps (Burgenland)/Diplomat', maxChars: 5 },
    BH: { name: 'Bundesheer/Military', maxChars: 5 },
    BI: { name: 'Bildungsdirektion/School', maxChars: 5 },
    BP: { name: 'Bundespolizei/Police', maxChars: 5 },
    FW: { name: 'Feuerwehr/Fire', maxChars: 5 },
    GK: { name: 'Konsularisches Korps (Graz)/Consulate', maxChars: 5 },
    JW: { name: 'Justizwache/Court', maxChars: 5 },
    KK: { name: 'Konsularisches Korps (Kärnten)/Consuate', maxChars: 5 },
    SK: { name: 'Konsularisches Korps (Salzburg)/Consulate', maxChars: 5 },
    TD: { name: 'Diplomatisches Korps (Tirol)/Diplomat', maxChars: 5 },
    TK: { name: 'Konsularisches Korps (Tirol)/Consulate', maxChars: 5 },
    VD: { name: 'Diplomatisches Korps (Vbg)/Consulate', maxChars: 5 },
    WD: { name: 'Diplomatisches Korps (Wien)/Diplomat', maxChars: 5 },
  }
};

/** The Bundesland a district code belongs to; undefined for special series and unknown codes. */
export function findAtState(districtCode: string | null | undefined): AtState | undefined {
  const code = districtCode?.trim().toUpperCase();
  return (code ? AT_REGIONS.districts[code]?.emblem : undefined) as AtState | undefined;
}
